/**
 * 会话传输实现：把 {@link ConversationTransport} 语义映射到真实 HTTP 路径。
 *
 * 约束来源：CONTRACT.md 第 24~32 行。
 * - before / after 互斥：客户端先拒绝，避免产生语义不清的请求；
 * - 附件必须经鉴权接口，绝不存在静态公共 URL；
 * - 幂等键与上下文快照原样透传（重试复用同一对象由上层状态机保证）。
 */

import { SupportError } from './errors';
import { decodeWireContext, encodeWireContext } from './context-wire';
import type { SupportApi } from './http';
import type {
  ConversationTransport,
  Message,
  MessageListQuery,
  MessageVisibility,
  PageContext,
  SendMessageBody,
  SupportAttachment,
} from './types';

function conversationPath(conversationId: string, suffix = ''): string {
  return `/conversations/${encodeURIComponent(conversationId)}${suffix}`;
}

/**
 * 消息响应里的快照还原：后端返回的是 wire 形状（pageId/title/period YYYYMM），
 * 绝不能原样漏给上层（上层按客户端形状读取 pageKey/YYYY-MM）。
 * 无快照（缺字段/null/非对象）就不带 context，绝不凭空造一个。
 */
function decodeWireMessage(value: Message): Message {
  const raw = value as unknown as Record<string, unknown>;
  const { context: wireContext, ...rest } = raw;
  const context = decodeWireContext(wireContext);
  return (context === undefined ? rest : { ...rest, context }) as unknown as Message;
}

/** 出站消息载荷：只有快照需要编码，其余字段原样透传（幂等键/文本/附件不变）。 */
function encodeSendMessageBody(body: SendMessageBody): Record<string, unknown> {
  const { context, ...rest } = body;
  return context === undefined ? { ...rest } : { ...rest, context: encodeWireContext(context) };
}

/** 上下文列表逐项还原；无法解析的项直接丢弃（不产出半成品上下文）。 */
function decodeWireContextList(items: unknown): PageContext[] {
  if (!Array.isArray(items)) return [];
  const out: PageContext[] = [];
  for (const item of items) {
    const decoded = decodeWireContext(item);
    if (decoded !== undefined) out.push(decoded);
  }
  return out;
}

export function createConversationTransport(api: SupportApi): ConversationTransport {
  return {
    async downloadAttachment(attachmentId: string): Promise<Blob> {
      return await api.downloadAttachment(attachmentId);
    },

    async listContexts(conversationId: string) {
      const result = await api.request<{ items: unknown[] }>(
        conversationPath(conversationId, '/contexts'),
      );
      return { items: decodeWireContextList(result.items) };
    },

    async listMessages(conversationId: string, query: MessageListQuery) {
      if (query.after !== undefined && query.before !== undefined) {
        throw new SupportError({
          code: 0,
          kind: 'protocol',
          message: 'after 与 before 互斥，不能同时指定',
        });
      }
      const page = await api.request<{
        hasMore: boolean;
        items: Message[];
        nextCursor: null | string;
      }>(
        conversationPath(conversationId, '/messages'),
        { query: { after: query.after, before: query.before, limit: query.limit } },
      );
      return { ...page, items: page.items.map(decodeWireMessage) };
    },

    async markRead(conversationId: string, cursor: string) {
      return await api.request<{ cursor: string }>(
        conversationPath(conversationId, '/read'),
        { body: { cursor }, method: 'POST' },
      );
    },

    async pushContext(conversationId: string, context: PageContext) {
      const result = await api.request<undefined | { accepted?: unknown; context?: unknown }>(
        conversationPath(conversationId, '/context'),
        { body: { context: encodeWireContext(context) }, method: 'POST' },
      );
      // accepted 只能来自服务端应答；缺失或类型不符按协议失败，绝不本地推断
      if (!result || typeof result.accepted !== 'boolean') {
        throw new SupportError({
          code: 0,
          kind: 'protocol',
          message: '上下文上报响应缺少合法的 accepted 字段',
        });
      }
      // 服务端权威快照必须完整可用：缺失/非法一律协议失败。
      // 绝不用调用方原始上下文回填"服务端返回"——那会把未经核实的 companyId/companyVerified
      // 当成服务端结论留给上层，并让"已核实"无从判定。
      const decoded = decodeWireContext(result.context);
      if (decoded === undefined) {
        throw new SupportError({
          code: 0,
          kind: 'protocol',
          message: '上下文上报响应缺少服务端上下文快照',
        });
      }
      return { accepted: result.accepted, context: decoded };
    },

    async sendMessage(conversationId: string, body: SendMessageBody) {
      const message = await api.request<Message>(conversationPath(conversationId, '/messages'), {
        body: encodeSendMessageBody(body),
        method: 'POST',
      });
      return decodeWireMessage(message);
    },

    async uploadAttachment(
      conversationId: string,
      file: Blob,
      visibility: MessageVisibility,
      fileName?: string,
    ): Promise<SupportAttachment> {
      return await api.uploadAttachment(conversationId, file, visibility, fileName);
    },
  };
}